// Sub User Section Component for Medical Application Form
import React, { useState, useEffect, useCallback } from 'react';

import {
    Box,
    Typography,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    TextField,
    CircularProgress,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Tooltip,
    Tabs,
    Tab,
    Snackbar,
    Chip,
    Avatar,
    Card,
    CardContent,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Autocomplete
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    Person as PersonIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Work as WorkIcon,
    CheckCircle as CheckIcon,
    Cancel as CancelIcon,
    Add as AddIcon,
    Group as GroupIcon,
    Key as KeyIcon,
    ContentCopy as CopyIcon,
    Block as BlockIcon,
    CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import subUserService from '../../../services/subUserService';

const SubUserSection = () => {
    const [tabValue, setTabValue] = useState(0);
    const [subUsers, setSubUsers] = useState([]);
    const [editingUser, setEditingUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [disciplines, setDisciplines] = useState([]);
    const [loadingDisciplines, setLoadingDisciplines] = useState(false);

    // Employee types from the lab staff section
    const employeeTypes = [
        'Details of Radiological Safety Officer (For Medical Imaging laboratories)',
        'Head of the laboratory having overall responsibility',
        'Laboratory Director',
        'Person responsible for management system',
        'Person Responsible for Technical Operations',
        'Others'
    ];

    // Snackbar state
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    // Form fields state
    const [formData, setFormData] = useState({
        salutation: '',
        name: '',
        designation: '',
        email: '',
        contactNo: '',
        disciplineIds: [], // Changed from disciplineId to disciplineIds array
        disciplineNames: [] // Store discipline names for display
    });

    // File upload state
    const [fileData, setFileData] = useState({});

    // Get application context
    const orgId = localStorage.getItem('orgId');

    // Show snackbar helper
    const showSnackbar = useCallback((message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    }, []);

    // Handle snackbar close
    const handleSnackbarClose = useCallback(() => {
        setSnackbar(prev => ({ ...prev, open: false }));
    }, []);

    // Copy to clipboard
    const copyToClipboard = useCallback(async (text, type) => {
        try {
            await navigator.clipboard.writeText(text);
            showSnackbar(`${type} copied to clipboard!`, 'success');
        } catch (error) {
            console.error('Failed to copy to clipboard:', error);
            showSnackbar('Failed to copy to clipboard', 'error');
        }
    }, [showSnackbar]);

    // Load data on component mount
    useEffect(() => {
        if (orgId) {
            loadSubUsers();
            loadDisciplines();
        }
    }, [orgId]);

    // Load available disciplines using existing API
    const loadDisciplines = async () => {
        try {
            setLoadingDisciplines(true);
            const appId = localStorage.getItem('appId');
            
            // Use existing API endpoint for fetching selected disciplines
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/testing-laboratory/disciplines/selection?appId=${appId}&orgId=${orgId}`
            );
            
            if (!response.ok) {
                throw new Error('Failed to fetch disciplines');
            }
            
            const data = await response.json();
            
            // Transform the response to match expected format
            // Assuming the API returns disciplines with id and DisciplineName
            const disciplinesList = data.data || data || [];
            setDisciplines(disciplinesList);
            
            console.log('Loaded disciplines:', disciplinesList);
        } catch (error) {
            console.error('Error loading disciplines:', error);
            showSnackbar('Error loading disciplines: ' + error.message, 'error');
            setDisciplines([]);
        } finally {
            setLoadingDisciplines(false);
        }
    };

    // Handle form field changes
    const handleFormDataChange = useCallback((field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    }, []);

    // Handle multiple discipline selection change
    const handleDisciplineChange = (event, newValue) => {
        // newValue is an array of discipline objects from Autocomplete
        const selectedIds = newValue.map(d => d.id || d.DisciplineId);
        const selectedNames = newValue.map(d => d.DisciplineName || d.disciplineName);
        
        setFormData(prev => ({
            ...prev,
            disciplineIds: selectedIds,
            disciplineNames: selectedNames
        }));
    };

    // Validate form
    const validateForm = () => {
        if (!formData.name || !formData.designation || 
            !formData.email || !formData.contactNo || formData.disciplineIds.length === 0) {
            showSnackbar('Please fill in all required fields including at least one discipline', 'warning');
            return false;
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            showSnackbar('Please enter a valid email address', 'warning');
            return false;
        }

        // Contact number validation
        if (formData.contactNo.length !== 10 || !/^\d+$/.test(formData.contactNo)) {
            showSnackbar('Please enter a valid 10-digit contact number', 'warning');
            return false;
        }

        return true;
    };

    // Save sub user
    const saveSubUser = async () => {
        if (!validateForm()) {
            return;
        }

        if (!orgId) {
            showSnackbar('Organization ID not found. Please refresh the page.', 'error');
            return;
        }

        try {
            setLoading(true);

            // Get labTypeId from localStorage
            const labTypeId = localStorage.getItem('labTypeId');

            // Prepare sub user data with multiple disciplines
            const subUserData = {
                orgId: parseInt(orgId),
                salutation: formData.salutation,
                name: formData.name,
                capacity: formData.designation,
                mobile: formData.contactNo,
                email: formData.email,
                labTypeId: labTypeId ? parseInt(labTypeId) : null,
                disciplineIds: formData.disciplineIds.map(id => parseInt(id)) // Send array of discipline IDs
            };

            // Call the sub user API
            const response = editingUser 
                ? await subUserService.updateSubUser(editingUser.Id, subUserData)
                : await subUserService.createSubUser(subUserData);

            if (editingUser) {
                showSnackbar('Sub user updated successfully', 'success');
            } else {
                // For new sub-user, show credentials in success message
                const credentials = response.data || {};
                const disciplineCount = formData.disciplineIds.length;
                showSnackbar(
                    `Sub user created successfully with ${disciplineCount} discipline(s)! Username: ${credentials.username}, Password: ${credentials.password}`,
                    'success'
                );
            }
            clearForm();
            loadSubUsers();
            setTabValue(1); // Switch to view tab
        } catch (error) {
            console.error('Error saving sub user:', error);
            showSnackbar('Error saving sub user: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    // Load sub users
    const loadSubUsers = async () => {
        try {
            setLoading(true);
            const response = await subUserService.getSubUsers(orgId);
            setSubUsers(response.data || []);
            console.log('Loaded sub users:', response.data || []);
        } catch (error) {
            console.error('Error loading sub users:', error);
            setSubUsers([]);
            showSnackbar('Error loading sub users: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    // Clear form
    const clearForm = useCallback(() => {
        setEditingUser(null);
        setFormData({
            salutation: '',
            name: '',
            designation: '',
            email: '',
            contactNo: '',
            disciplineIds: [],
            disciplineNames: []
        });
    }, []);

    // Handle edit
    const handleEdit = (user) => {
        setEditingUser(user);
        setFormData({
            salutation: user.Salutation || '',
            name: user.Name || '',
            designation: user.Capacity || '',
            email: user.Email || '',
            contactNo: user.Mobile || '',
            disciplineIds: user.disciplineIds || [], // Use array from backend
            disciplineNames: user.disciplineNames ? user.disciplineNames.split(', ') : []
        });
        setTabValue(0); // Switch to add/edit tab
    };

    // Handle delete
    const handleDelete = async (user) => {
        if (window.confirm('Are you sure you want to delete this sub user?')) {
            try {
                await subUserService.deleteSubUser(user.Id);
                showSnackbar('Sub user deleted successfully', 'success');
                loadSubUsers();
            } catch (error) {
                console.error('Error deleting sub user:', error);
                showSnackbar('Error deleting sub user: ' + error.message, 'error');
            }
        }
    };

    // Handle activate sub user
    const handleActivate = async (user) => {
        const confirmMessage = `Are you sure you want to activate ${user.Name}?`;
        
        if (window.confirm(confirmMessage)) {
            try {
                await subUserService.activateSubUser(user.Id);
                showSnackbar(`Sub user activated successfully`, 'success');
                loadSubUsers();
            } catch (error) {
                console.error(`Error activating sub user:`, error);
                showSnackbar(`Error activating sub user: ` + error.message, 'error');
            }
        }
    };

    // Handle deactivate sub user
    const handleDeactivate = async (user) => {
        const confirmMessage = `Are you sure you want to deactivate ${user.Name}? They will not be able to log in.`;
        
        if (window.confirm(confirmMessage)) {
            try {
                await subUserService.deactivateSubUser(user.Id);
                showSnackbar(`Sub user deactivated successfully`, 'success');
                loadSubUsers();
            } catch (error) {
                console.error(`Error deactivating sub user:`, error);
                showSnackbar(`Error deactivating sub user: ` + error.message, 'error');
            }
        }
    };

    // Tab change handler
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        if (newValue === 0) {
            clearForm();
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <GroupIcon sx={{ mr: 2, color: '#1976d2', fontSize: 32 }} />
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
                    Sub User Management
                </Typography>
            </Box>

            {/* Tabs */}
            <Paper sx={{ mb: 3 }}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                    <Tab 
                        label={editingUser ? "Edit Sub User" : "Create Sub User"} 
                        icon={editingUser ? <EditIcon /> : <AddIcon />} 
                        iconPosition="start"
                        sx={{ fontWeight: 600 }} 
                    />
                    <Tab 
                        label="Existing Sub Users" 
                        icon={<VisibilityIcon />} 
                        iconPosition="start"
                        sx={{ fontWeight: 600 }} 
                    />
                </Tabs>
            </Paper>

            {/* Tab Content */}
            {tabValue === 0 && (
                <Paper sx={{ p: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', borderRadius: 2 }}>
                    {editingUser && (
                        <Alert severity="info" sx={{ mb: 3 }}>
                            <Typography variant="body1">
                                Editing sub user: <strong>{editingUser.name}</strong>
                            </Typography>
                            <Button
                                size="small"
                                startIcon={<CancelIcon />}
                                onClick={clearForm}
                                sx={{ mt: 1 }}
                            >
                                Cancel Edit
                            </Button>
                        </Alert>
                    )}

                    <Typography variant="h6" sx={{ mb: 3, color: '#334155', fontWeight: 600 }}>
                        {editingUser ? 'Edit Sub User Details' : 'Create New Sub User'}
                    </Typography>

                    <Grid container spacing={3}>
                        {/* Personal Information */}
                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{ mb: 2, color: '#667eea', fontWeight: 600 }}>
                                Personal Information
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>Salutation</InputLabel>
                                <Select
                                    value={formData.salutation}
                                    label="Salutation"
                                    onChange={(e) => handleFormDataChange('salutation', e.target.value)}
                                >
                                    <MenuItem value="Mr.">Mr.</MenuItem>
                                    <MenuItem value="Ms.">Ms.</MenuItem>
                                    <MenuItem value="Dr.">Dr.</MenuItem>
                                    <MenuItem value="Prof.">Prof.</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} md={9}>
                            <TextField
                                fullWidth
                                label="Name"
                                value={formData.name}
                                onChange={(e) => handleFormDataChange('name', e.target.value)}
                                required
                                helperText="Enter full name of the sub user"
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth required>
                                <InputLabel>Designation</InputLabel>
                                <Select
                                    value={formData.designation}
                                    label="Designation"
                                    onChange={(e) => handleFormDataChange('designation', e.target.value)}
                                >
                                    <MenuItem value="">Select Designation</MenuItem>
                                    {employeeTypes.map((type, index) => (
                                        <MenuItem key={index} value={type}>
                                            {type}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Discipline Selection - Multi-select */}
                        <Grid item xs={12} md={6}>
                            <Autocomplete
                                multiple
                                id="discipline-select"
                                options={disciplines}
                                getOptionLabel={(option) => option.DisciplineName || option.disciplineName || ''}
                                value={disciplines.filter(d => 
                                    formData.disciplineIds.includes(d.id || d.DisciplineId)
                                )}
                                onChange={handleDisciplineChange}
                                disabled={loadingDisciplines}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Disciplines"
                                        placeholder="Select disciplines"
                                        required
                                        helperText={
                                            loadingDisciplines 
                                                ? "Loading disciplines..." 
                                                : disciplines.length === 0 
                                                    ? "No disciplines found. Please add disciplines in the Discipline section first."
                                                    : "Select one or more disciplines for this sub-user"
                                        }
                                    />
                                )}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip
                                            key={option.id || option.DisciplineId}
                                            label={option.DisciplineName || option.disciplineName}
                                            {...getTagProps({ index })}
                                            color="primary"
                                            size="small"
                                        />
                                    ))
                                }
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Email"
                                type="email"
                                value={formData.email}
                                onChange={(e) => handleFormDataChange('email', e.target.value)}
                                required
                                helperText="Enter valid email address"
                            />
                        </Grid>

                        {/* Contact Number */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Contact Number"
                                type="tel"
                                value={formData.contactNo}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                                    handleFormDataChange('contactNo', value);
                                }}
                                required
                                helperText="Enter 10-digit mobile number"
                                inputProps={{ maxLength: 10 }}
                            />
                        </Grid>

                        {/* Auto-generated credentials info */}
                        {!editingUser && (
                            <Grid item xs={12}>
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    <Typography variant="body2">
                                        <strong>Note:</strong> Username and password will be automatically generated when you create the sub user.
                                        The username will be based on your current username with a sequential number (e.g., nabln000202_1).
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        <strong>Important:</strong> The sub user will only have access to the discipline you select here and
                                        can only manage data for that specific discipline and proficiency testing.
                                    </Typography>
                                </Alert>
                            </Grid>
                        )}

                        {/* Action Buttons */}
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                                <Button
                                    variant="outlined"
                                    onClick={clearForm}
                                    disabled={loading}
                                >
                                    Clear
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={saveSubUser}
                                    disabled={loading}
                                    startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
                                    sx={{
                                        minWidth: 140,
                                        backgroundColor: '#1976d2',
                                        '&:hover': { backgroundColor: '#1565c0' }
                                    }}
                                >
                                    {loading ? 'Saving...' : (editingUser ? 'Update Sub User' : 'Create Sub User')}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {/* Existing Sub Users Tab */}
            {tabValue === 1 && (
                <Paper sx={{ p: 3, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ mb: 3, color: '#334155', fontWeight: 600 }}>
                        Existing Sub Users
                    </Typography>

                    {subUsers.length === 0 ? (
                        <Alert severity="info" sx={{ textAlign: 'center' }}>
                            <Typography variant="body1">
                                No sub users found. Create your first sub user using the "Create Sub User" tab.
                            </Typography>
                        </Alert>
                    ) : (
                        <TableContainer component={Paper} sx={{ boxShadow: 1 }}>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                                        <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Discipline</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Capacity</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Contact</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Username</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Password</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {subUsers.map((user, index) => (
                                        <TableRow key={index} hover>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Avatar sx={{ mr: 2, bgcolor: '#1976d2' }}>
                                                        <PersonIcon />
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {user.Salutation} {user.Name}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {user.disciplines && user.disciplines.length > 0 ? (
                                                        user.disciplines.map((disc, idx) => (
                                                            <Chip 
                                                                key={idx}
                                                                label={disc.DisciplineName}
                                                                color="primary"
                                                                size="small"
                                                                sx={{ fontWeight: 500 }}
                                                            />
                                                        ))
                                                    ) : (
                                                        <Chip 
                                                            label={user.DisciplineName || 'N/A'}
                                                            color="default"
                                                            size="small"
                                                            sx={{ fontWeight: 500 }}
                                                        />
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {user.Capacity}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <EmailIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                                                    <Typography variant="body2">
                                                        {user.Email}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <PhoneIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                                                    <Typography variant="body2">
                                                        {user.Mobile}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                                                        {user.Username}
                                                    </Typography>
                                                    <Tooltip title="Copy Username">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => copyToClipboard(user.Username, 'Username')}
                                                            sx={{ color: '#1976d2' }}
                                                        >
                                                            <CopyIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <KeyIcon sx={{ fontSize: 16, color: '#d32f2f' }} />
                                                    <Typography variant="body2" sx={{ 
                                                        fontFamily: 'monospace', 
                                                        fontWeight: 600, 
                                                        color: '#d32f2f',
                                                        backgroundColor: '#ffebee',
                                                        padding: '4px 8px',
                                                        borderRadius: '4px',
                                                        border: '1px solid #ffcdd2'
                                                    }}>
                                                        {user.password}
                                                    </Typography>
                                                    <Tooltip title="Copy Password">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => copyToClipboard(user.password, 'Password')}
                                                            sx={{ color: '#d32f2f' }}
                                                        >
                                                            <CopyIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                {user.SubuserActive === "1" || user.SubuserActive === true ? (
                                                    <Chip 
                                                        icon={<CheckCircleIcon />}
                                                        label="Active" 
                                                        color="success" 
                                                        size="small"
                                                        sx={{ fontWeight: 500 }}
                                                    />
                                                ) : (
                                                    <Chip 
                                                        icon={<BlockIcon />}
                                                        label="Inactive" 
                                                        color="error" 
                                                        size="small"
                                                        sx={{ fontWeight: 500 }}
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    {/* Show Deactivate button if active, Activate button if inactive */}
                                                    {(user.SubuserActive === 1 || user.SubuserActive === "1" || user.SubuserActive === true) ? (
                                                        <Tooltip title="Deactivate">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDeactivate(user)}
                                                                sx={{ color: '#f57c00' }}
                                                            >
                                                                <BlockIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    ) : (
                                                        <Tooltip title="Activate">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleActivate(user)}
                                                                sx={{ color: '#2e7d32' }}
                                                            >
                                                                <CheckCircleIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    <Tooltip title="Edit">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleEdit(user)}
                                                            sx={{ color: '#1976d2' }}
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDelete(user)}
                                                            sx={{ color: '#d32f2f' }}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Paper>
            )}

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default SubUserSection;